import React from "react";
import { Upload, PencilLine } from "lucide-react";
import { TESTIDS } from "@/lib/testIds";

/**
 * StartChoice — the entry surface for the Create Cruise workflow.
 * Two paths (Upload PDF · Enter manually) that both converge on the
 * same review + commit flow downstream (per PO directive 2026-02-18).
 */
export default function StartChoice({ onUploadClick, onManualClick }) {
  return (
    <div className="px-8 py-9" data-testid={TESTIDS.createCruiseStart}>
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl mb-8">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(6,26,57,0.85) 0%, rgba(15,82,198,0.62) 40%, rgba(15,82,198,0.35) 100%), url('https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&w=1600&q=60')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="relative px-7 py-9 text-white">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-black">
            Plan your voyage
          </div>
          <h2 className="font-display text-[30px] leading-tight font-semibold mt-2 max-w-lg">
            Where are we sailing next?
          </h2>
          <p className="text-[13.5px] text-white/85 mt-2 max-w-md leading-relaxed">
            Add your next cruise and Cruise Command will assemble your journey —
            itinerary, travelers, dining, documents and more — all in one place.
          </p>
        </div>
      </div>

      <button
        onClick={onUploadClick}
        className="w-full flex items-start gap-4 p-5 rounded-xl border-2 border-amber-500 bg-amber-50/70 hover:bg-amber-50 text-left transition-all"
        data-testid={TESTIDS.createCruiseChooseUpload}
      >
        <div className="w-11 h-11 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
          <Upload className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-[17px] font-bold text-slate-900">
              Upload booking confirmation
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
              Recommended
            </span>
          </div>
          <div className="text-[13px] text-slate-600 mt-1">
            Drop your booking PDF — we'll extract every detail and confirm with you before saving.
          </div>
        </div>
      </button>

      <div className="flex items-center gap-3 my-5 text-[12px] uppercase tracking-widest text-slate-400">
        <div className="flex-1 h-px bg-slate-200" />
        or
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <button
        onClick={onManualClick}
        className="w-full flex items-start gap-4 p-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-left transition-all"
        data-testid={TESTIDS.createCruiseChooseManual}
      >
        <div className="w-11 h-11 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
          <PencilLine className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-display text-[17px] font-bold text-slate-900">
            Enter cruise details manually
          </div>
          <div className="text-[13px] text-slate-600 mt-1">
            No confirmation yet? Type in what you know — you can enrich it later.
          </div>
        </div>
      </button>

      <div className="mt-6 text-center text-[11px] text-slate-400 uppercase tracking-[0.14em] font-semibold">
        Coming soon · Forward a booking email · Import from Cruise Planner · Add a Future Cruise Certificate
      </div>
    </div>
  );
}
